const professionalTableColumns = [
  'id',
  'professional_id: professionalId',
  'first_name: firstName',
  'last_name: lastName',
  'email',
  'mobile_number: mobileNumber',
  'is_active: isActive',
  'status',
  'avatar',
  'created_at: createdAt',
  'updated_at: updatedAt',
  'fb_uid: fbUid',
  'address_line_1: addressLine1',
  'province',
  'postal_code: postalCode',
  'about',
  'nic_image: nicImage',
  'address_proof: addressProof',
].join('\n    ');

const operation = (query, variables) => ({ query, variables });

const professionalsQuery = `query Professionals($where: ProfessionalWhereInput) {
  Professionals: professionals(where: $where) {
    ${professionalTableColumns}
  }
}`;

const ProfessionalsQueryAdministrator = operation(professionalsQuery, {
  where: {},
});

const ActiveProfessionalsQuery = operation(professionalsQuery, {
  where: { isActive: true, status: 'ACCEPTED' },
});

const RequestedProfessionalsQueryAdmin = operation(professionalsQuery, {
  where: { status: 'REQUESTED' },
});

// Public homepage carousel ("Get inspired with works made by our professionals").
const FeaturedProfessionalsQuery = operation(
  `query FeaturedProfessionals($first: Int) {
    featuredProfessionals(first: $first) {
      id
      firstName
      lastName
      avatar
      service {
        id
        serviceId
        name
        bannerImage
      }
    }
  }`,
  { first: 12 }
);

const BookingOrderHistory = operation(`query BookingOrderHistory {
  Orders: orders {
    id
    orderRef
    date: orderDate
    status: orderStatus
    note: serviceNote
    reference_id: referenceId
    created_at: createdAt
    Service: service {
      name
      Professional: professional {
        first_name: firstName
        last_name: lastName
        professional_id: professionalId
      }
    }
    EventBooker: eventBooker {
      event_booker_id: userId
      first_name: firstName
      last_name: lastName
    }
  }
}`);

const FindProfessional = ({ professionalUniqueID }) =>
  operation(
    `query FindProfessional($where: ProfessionalWhereUniqueInput!) {
      Professional: professional(where: $where) {
        ${professionalTableColumns}
      }
    }`,
    { where: { id: professionalUniqueID } }
  );

const FindProfessionalByMobileNo = ({ mobileNumber }) =>
  operation(
    `query FindProfessionalByMobileNo($where: ProfessionalWhereUniqueInput!) {
      Professional: professional(where: $where) {
        id
        mobileNumber
      }
    }`,
    { where: { mobileNumber } }
  );

// Public — professional registration duplicate check (no auth, no PII).
const CheckProfessionalContact = ({ email, mobileNumber }) =>
  operation(
    `query CheckProfessionalContact($email: String!, $mobileNumber: String!) {
      checkProfessionalContact(email: $email, mobileNumber: $mobileNumber) {
        emailTaken
        mobileTaken
      }
    }`,
    { email, mobileNumber }
  );

const PaymentsQuery = operation(`query Payments {
  payments {
    id
    paymentId
    orderId
    amount
    paymentType
    currency
    status
    paymentMethod
    transactionId
    createdAt
    updatedAt
  }
}`);

const OrdersQuery = operation(`query Orders {
  Orders: orders {
    id
    orderRef
    time: orderTime
    date: orderDate
    status: orderStatus
    note: serviceNote
    reference_id: referenceId
    created_at: createdAt
    Service: service {
      name
      Professional: professional {
        first_name: firstName
        last_name: lastName
        professional_id: professionalId
      }
    }
    EventBooker: eventBooker {
      event_booker_id: userId
      first_name: firstName
      last_name: lastName
    }
  }
}`);

const CompanyInformationQuery = operation(`query CompanyInformation {
  companyInformation {
    email
    phoneNumber
    socialMedia
    website
    name
    logo
    bannerImage
  }
}`);

const FindOrdersByOrderStatusAdmin = ({ status }) =>
  operation(
    `query FindOrdersByOrderStatusAdmin($where: OrderWhereInput) {
      Orders: orders(where: $where) {
        id
      }
    }`,
    { where: { status } }
  );

const FindUserOrders = ({ id }) =>
  operation(`query FindUserOrders {
    Orders: orders {
      id
      orderRef
      date: orderDate
      time: orderTime
      status: orderStatus
      note: serviceNote
      reference_id: referenceId
      created_at: createdAt
      Service: service {
        name
        Professional: professional {
          first_name: firstName
          last_name: lastName
          professional_id: professionalId
        }
      }
      EventBooker: eventBooker {
        event_booker_id: userId
        first_name: firstName
        last_name: lastName
      }
    }
  }`);

const FindProfessionalOrdersByOrderStatus = ({ orderStatus }) =>
  operation(
    `query FindProfessionalOrdersByOrderStatus($status: OrderStatus) {
      professionalOrders(status: $status) {
        id
        orderRef
        date
        time
        note
        place
        referenceId
        status
        total
        eventBooker {
          firstName
          lastName
          mobileNumber
        }
        servicePackage {
          name
          price
        }
      }
    }`,
    { status: Array.isArray(orderStatus) ? orderStatus[0] : orderStatus }
  );

const FindUserOrdersByOrderStatus = ({ userID, orderStatus }) =>
  operation(
    `query FindUserOrdersByOrderStatus($where: UserOrderWhereInput) {
      Orders: userOrders(where: $where) {
        id
        date
        time
        status
        note
        reference_id: referenceId
        is_active: isActive
        created_at: createdAt
        updated_at: updatedAt
      }
    }`,
    { where: { status: Array.isArray(orderStatus) ? orderStatus[0] : orderStatus } }
  );

const FindUserUnReviewedOrders = ({ userId }) =>
  operation(`query FindUserUnReviewedOrders {
    UnReviewedOrders: unReviewedOrders {
      id
      orderRef
      date
      reference_id: referenceId
    }
  }`);

const UsersQuery = operation(`query Users {
  EventBookers: users {
    id
    event_booker_id: userId
    email
    first_name: firstName
    last_name: lastName
    address_line_1: addressLine1
    province
    postal_code: postalCode
    mobile_number: mobileNumber
    is_subscribed: isSubscribed
    is_active: isActive
  }
}`);

const ServicesQuery = operation(
  `query Services($where: ServiceWhereInput) {
  Services: services(where: $where) {
    id
    service_id: serviceId
    description
    name
    banner_image: bannerImage
    icon
    is_popular: isPopular
    professional_id: professionalId
    professional_name: professionalName
    category_name: categoryName
    category_slug: categorySlug
    location
    facebook_url: facebookUrl
    website_url: websiteUrl
  }
}`,
  { where: { isActive: true } }
);

const PopularServicesQuery = operation(
  `query PopularServices($where: ServiceWhereInput) {
  Services: services(where: $where) {
    id
    service_id: serviceId
    description
    name
    banner_image: bannerImage
  }
}`,
  { where: { isPopular: true, isActive: true } }
);

const SendCode = ({ email, template }) =>
  operation(
    `mutation SendCode($email: String!, $template: String!) {
      SendCode(data: { email: $email, template: $template }) {
        referenceId
      }
    }`,
    { email, template }
  );

const VerifyCode = ({ referenceId, code }) =>
  operation(
    `mutation VerifyCode($referenceId: String!, $code: String!) {
      VerifyCode(data: { reference_id: $referenceId, code: $code }) {
        status
      }
    }`,
    { referenceId, code }
  );

const FindActiveServicesByName = ({ name }) =>
  operation(
    `query FindActiveServicesByName($where: ServiceWhereInput) {
      Services: services(where: $where) {
        id
        service_id: serviceId
        name
        icon
        banner_image: bannerImage
        description
      }
    }`,
    { where: { name, isActive: true } }
  );

// Admin — a professional's services (used to exclude categories already taken).
const AdminProfessionalServices = ({ id }) =>
  operation(
    `query AdminProfessionalServices($where: ProfessionalWhereUniqueInput!) {
      Professional: professional(where: $where) {
        id
        services {
          id
          categoryId
        }
      }
    }`,
    { where: { id } }
  );

// Professional dashboard — the caller's own services with category + packages.
const ProfessionalServices = operation(`query ProfessionalServices {
  professionalServices {
    id
    serviceId
    name
    location
    websiteUrl
    isActive
    isPopular
    categoryId
    category {
      id
      name
      slug
    }
    packages {
      id
      servicePackageId
      name
      price
      description
      features
      isActive
    }
  }
}`);

// Public — dynamic, admin-managed categories (homepage section + dropdowns).
const GetCategories = operation(`query GetCategories {
  categories {
    id
    name
    slug
    description
    icon
    showHome
  }
}`);

// Admin — every category with its linked-service count.
const AdminGetCategories = operation(`query AdminGetCategories {
  categories {
    id
    name
    slug
    description
    icon
    isActive
    isProtected
    showHome
    serviceCount
  }
}`);

// Public — services in a category, by slug (category detail page).
const FindServicesByCategorySlug = ({ slug }) =>
  operation(
    `query FindServicesByCategorySlug($slug: String!) {
      Services: servicesByCategorySlug(slug: $slug) {
        id
        service_id: serviceId
        name
        professional_name: professionalName
        icon
        banner_image: bannerImage
        description
      }
    }`,
    { slug }
  );

const FindReviewsByProfessional = ({ professionalId }) =>
  operation(`query FindReviewsByProfessional {
    Reviews: userReviews {
      id
      rating
      description
      is_active: isActive
      created_at: createdAt
      updated_at: updatedAt
    }
  }`);

const FindServicesByProfessional = ({ professionalID }) =>
  operation(
    `query FindServicesByProfessional($where: ServiceWhereInput) {
      services(where: $where) {
        id
        accountName
        bankName
        bankBranchName
        accountNumber
      }
    }`,
    { where: { professionalId: professionalID } }
  );

const FindProfessionalService = ({ id }) =>
  operation(
    `query FindProfessionalService($where: ServiceWhereInput) {
      services(where: $where) {
        id
        name
        category_id: categoryId
        icon
        banner_image: bannerImage
        images
        hotline_number: hotlineNumber
        location
        facebook_url: facebookUrl
        website_url: websiteUrl
        description
      }
    }`,
    { where: { professionalId: id } }
  );

const FindUser = ({ userID }) =>
  operation(
    `query FindUser($where: UserWhereUniqueInput!) {
      EventBooker: user(where: $where) {
        first_name: firstName
        last_name: lastName
        address_line_1: addressLine1
        province
        postal_code: postalCode
        mobile_number: mobileNumber
        is_active: isActive
      }
    }`,
    { where: { id: userID } }
  );

const FindService = ({ id }) =>
  operation(
    `query FindService($where: ServiceWhereUniqueInput!) {
      Service: service(where: $where) {
        name
        service_id: serviceId
        id
        category_id: categoryId
        professional_id: professionalId
        hotline_number: hotlineNumber
        images
        description
        icon
        banner_image: bannerImage
        website_url: websiteUrl
        is_active: isActive
        packages {
          id
          servicePackageId
          name
          price
          description
          features
          isActive
        }
      }
    }`,
    { where: { id } }
  );

// Public — aggregates everything the seller detail page needs in one call.
const SellerProfileQuery = ({ serviceId }) =>
  operation(
    `query SellerProfile($serviceId: ID!) {
      sellerProfile(serviceId: $serviceId) {
        service {
          id
          name
          icon
          bannerImage
          description
          websiteUrl
          images
          categorySlug
          categoryName
        }
        professional {
          firstName
          lastName
          about
        }
        servicePackages {
          id
          servicePackageId
          name
          price
          description
          features
        }
        reviews {
          id
          rating
          description
          reviewer
        }
      }
    }`,
    { serviceId }
  );

const FindUserByEmail = ({ email }) =>
  operation(
    `query FindUserByEmail($where: UserWhereUniqueInput!) {
      EventBooker: user(where: $where) {
        id
      }
    }`,
    { where: { email } }
  );

const CheckEmailCanUsedForRegister = ({ email, type }) =>
  operation(
    `query CheckEmailCanUsedForRegister($where: CheckEmailCanUsedForRegisterWhereInput!) {
      CheckEmailCanUsedForRegister(where: $where) {
        success
        errorMessage
      }
    }`,
    { where: { email, type } }
  );

const FindUserByMobileNumber = ({ mobileNumber }) =>
  operation(
    `query FindUserByMobileNumber($where: UserWhereUniqueInput!) {
      EventBooker: user(where: $where) {
        id
      }
    }`,
    { where: { mobileNumber } }
  );

const FindProfessionalByEmail = ({ email }) =>
  operation(
    `query FindProfessionalByEmail($where: ProfessionalWhereUniqueInput!) {
      Professional: professional(where: $where) {
        id
        status
        isActive
      }
    }`,
    { where: { email } }
  );

const FindAdminByEmail = ({ email }) =>
  operation(
    `query FindAdminByEmail($where: AdminWhereInput) {
      admins(where: $where) {
        id
        isActive
      }
    }`,
    { where: { email } }
  );

const CurrentAdminQuery = operation(`query CurrentAdmin {
  currentAdmin {
    id
    email
    role
    isActive
  }
}`);

const CurrentUserQuery = operation(`query CurrentUser {
  currentUser {
    id
    email
    isActive
  }
}`);

const FindProfessionalByFbUid = ({ fbUid }) =>
  operation(
    `query FindProfessionalByFbUid($where: ProfessionalWhereUniqueInput!) {
      Professional: professional(where: $where) {
        id
        status
        isActive
      }
    }`,
    { where: { fbUid } }
  );

const FindOrdersByIDS = ({ ids }) =>
  operation(
    `query FindOrdersByIDS($where: OrderWhereInput) {
      orders(where: $where) {
        id
        orderDate
      }
    }`,
    { where: { id: { in: ids } } }
  );

const FindPackagesByProfessional = ({ id }) =>
  operation(`query FindPackagesByProfessional {
    ServicePackages: findPackagesByProfessional {
      id
      price
      name
      isActive
      description
      serviceId
      servicePackageId
    }
  }`);

const PasswordChange = ({ password }) =>
  operation(
    `mutation PasswordChange($password: String) {
      passwordChange(password: $password) {
        id
      }
    }`,
    { password }
  );

export {
  ProfessionalsQueryAdministrator,
  FindProfessional,
  FindProfessionalByMobileNo,
  FindProfessionalByEmail,
  CheckProfessionalContact,
  RequestedProfessionalsQueryAdmin,
  ActiveProfessionalsQuery,
  FeaturedProfessionalsQuery,
  SellerProfileQuery,
  BookingOrderHistory,
  PaymentsQuery,
  OrdersQuery,
  FindOrdersByOrderStatusAdmin,
  FindUserOrders,
  FindProfessionalOrdersByOrderStatus,
  FindUserOrdersByOrderStatus,
  FindUserUnReviewedOrders,
  UsersQuery,
  ServicesQuery,
  PopularServicesQuery,
  FindActiveServicesByName,
  AdminProfessionalServices,
  ProfessionalServices,
  GetCategories,
  AdminGetCategories,
  FindServicesByCategorySlug,
  FindReviewsByProfessional,
  FindServicesByProfessional,
  FindUser,
  FindService,
  FindUserByEmail,
  FindUserByMobileNumber,
  FindAdminByEmail,
  CurrentAdminQuery,
  CurrentUserQuery,
  FindProfessionalByFbUid,
  FindOrdersByIDS,
  FindPackagesByProfessional,
  SendCode,
  VerifyCode,
  PasswordChange,
  CompanyInformationQuery,
  CheckEmailCanUsedForRegister,
  FindProfessionalService,
};
